import * as React from "react";
import * as React44ms from "../../Util/form-utils/react-44ms";

import {
  ACountry,
  CheckboxInput2023,
  CheckboxInput2023Expects,
  CheckboxInput2023Props,
  useLocalizationProvider,
} from "../../Common";

import {
  useDataMutableSourceShippingClientStorageData,
  useDeliveryDetailsUserExperience_asController,
  useMyBagPresentationModel,
} from "../providers";

import { BillingAddressFormExpects } from "../model";

import { AddressFormFragment } from "./delivery-details-address-form-fragment";
import { ReactCheckout2022_AddressFormTypes } from "./delivery-details-address-form-types";
import { useMyPreviouslyFilledInInfo } from "./use-my-previously-filled-in-info";

interface BillingProps {
  formRef: React44ms.Api.TypedRef<BillingAddressFormExpects>;
  formRef_isBillingAndShippingTheSame: React44ms.Api.TypedRef<CheckboxInput2023Expects>;
}
export const Billing: React.FunctionComponent<BillingProps> = (props) => {
  const { countryCode: theCountryIAmCurrentlyTryingToCheckoutOn } =
    useLocalizationProvider();
  const { presentationMyBagOrder } = useMyBagPresentationModel();

  const {
    handleLastSelected_isBillingAndShippingTheSameChange:
      deliveryDetailsUserExperience_asController_handleLastSelected_isBillingAndShippingTheSameChange,
  } = useDeliveryDetailsUserExperience_asController();

  const [greaterFormState] = React44ms.React.useForm(
    props.formRef_isBillingAndShippingTheSame
  );
  const { value: isBillingAndShippingTheSame } = greaterFormState || {};

  useRememberMyDetails(props.formRef_isBillingAndShippingTheSame);

  const shouldRestrictToCountry = React.useMemo(() => {
    if (presentationMyBagOrder?.isThisAVirtualOrder) {
      return theCountryIAmCurrentlyTryingToCheckoutOn as ACountry;
    }
  }, [
    presentationMyBagOrder?.isThisAVirtualOrder,
    theCountryIAmCurrentlyTryingToCheckoutOn,
  ]);

  const handleLastSelected_isBillingAndShippingTheSameChange: CheckboxInput2023Props["onChange"] =
    React.useCallback(
      (formRef, value) => {
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          formRef,
          value
        );

        deliveryDetailsUserExperience_asController_handleLastSelected_isBillingAndShippingTheSameChange(
          value
        );
      },
      [
        deliveryDetailsUserExperience_asController_handleLastSelected_isBillingAndShippingTheSameChange,
      ]
    );

  return (
    <React.Fragment>
      {!presentationMyBagOrder?.isThisAVirtualOrder ? (
        <CheckboxInput2023
          formRef={props.formRef_isBillingAndShippingTheSame}
          onChange={handleLastSelected_isBillingAndShippingTheSameChange}
          id={"isBillingAndShippingTheSame"}
        >{`My billing and shipping address are the same`}</CheckboxInput2023>
      ) : null}

      {presentationMyBagOrder?.isThisAVirtualOrder ||
      !isBillingAndShippingTheSame ? (
        <AddressFormFragment
          addressFormType={ReactCheckout2022_AddressFormTypes.billing}
          title="Billing Address"
          formRef={props.formRef}
          formRef_isBillingAndShippingTheSame={
            props.formRef_isBillingAndShippingTheSame
          }
          shouldRestrictToCountry={shouldRestrictToCountry}
        />
      ) : null}
    </React.Fragment>
  );
};

const useRememberMyDetails = (
  formRef: React44ms.Api.TypedRef<CheckboxInput2023Expects>
) => {
  const shippingClientStorageData =
    useDataMutableSourceShippingClientStorageData();

  const { shouldMigrateDeliveryDetails, handleMigrationComplete } =
    useMyPreviouslyFilledInInfo();
  const handleMigration = React.useCallback(() => {
    const { lastSelected_isBillingAndShippingTheSame } =
      shippingClientStorageData;
    if (
      lastSelected_isBillingAndShippingTheSame !== undefined &&
      lastSelected_isBillingAndShippingTheSame !== null
    ) {
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        formRef,
        !!lastSelected_isBillingAndShippingTheSame
      );
    }
  }, [
    formRef,
    shippingClientStorageData.lastSelected_isBillingAndShippingTheSame,
  ]);
  React.useEffect(() => {
    if (shouldMigrateDeliveryDetails) {
      handleMigration();
      handleMigrationComplete();
    }
  }, [shouldMigrateDeliveryDetails, handleMigration, handleMigrationComplete]);
};
